import React from 'react';

import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { PricingPlanTickRateFormSchemaValues } from 'src/features/pricingPlan/utils/formSchema';
import {
    DAY_OF_WEEK_TRANSLATIONS_MAP,
} from 'src/features/pricingPlan/utils/constants';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { Form, FormControl, FormControlStack, FormItem, FormLabel, FormMessage, FormField } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import {
    MultiSelect,
    MultiSelectContent,
    MultiSelectControl,
    MultiSelectOption,
    MultiSelectTrigger
} from 'src/registry/ui/MultiSelect';
import { DAY_OF_WEEK } from 'src/features/pricingPlan/utils/types';

type TickRateFormProps = {
    form: UseFormReturn<PricingPlanTickRateFormSchemaValues>;
    onSubmit: (values: PricingPlanTickRateFormSchemaValues) => void;
    isCreate: boolean;
};

function TickRateForm({ onSubmit, form, isCreate }: TickRateFormProps) {
    const { formatMessage } = useIntl();
    const [displayRate, setDisplayRate] = React.useState('');

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            {
                isCreate ? (
                    <FormField
                        control={form.control}
                        name="day_of_week"
                        render={({ field }) => {
                            const { ref: _, ...rest } = field;
                            const daysOfWeekArray = Array.isArray(field.value)
                                ? field.value
                                : [];
                            const fieldData = daysOfWeekArray?.map((identifier: DAY_OF_WEEK) => {
                                    const [, value] = Object.entries(DAY_OF_WEEK_TRANSLATIONS_MAP).find(
                                        ([key]) => key as DAY_OF_WEEK === identifier,
                                    )!;

                                    return value ? formatMessage(value) : null;
                                })
                                .join(', ');

                            const allValues: DAY_OF_WEEK[] = Object.entries(DAY_OF_WEEK_TRANSLATIONS_MAP).map(([key]) => key as DAY_OF_WEEK);
                            return (
                                <FormItem variant="dialog">
                                    <FormLabel variant="dialog" required>
                                        <FormattedMessage {...pricingPlanMessages.day} />
                                    </FormLabel>
                                    <FormControlStack>
                                        <FormControl>
                                            <MultiSelect {...rest}>
                                                <MultiSelectTrigger asChild>
                                                    <MultiSelectControl className="-mr-4 w-full">
                                                        {fieldData}
                                                        {fieldData?.length == 0 && (
                                                            <span className="text-gray-400">
                                                        {`${formatMessage(pricingPlanMessages.day)}...`}
                                                    </span>
                                                        )}
                                                    </MultiSelectControl>
                                                </MultiSelectTrigger>
                                                <MultiSelectContent allValues={allValues}>
                                                    {Object.entries(DAY_OF_WEEK_TRANSLATIONS_MAP).map(([key, value]) => (
                                                        <MultiSelectOption key={key} selected={key}>
                                                            <div className="flex flex-row justify-between">
                                                                {formatMessage(value)}
                                                            </div>
                                                        </MultiSelectOption>
                                                    ))}
                                                </MultiSelectContent>
                                            </MultiSelect>
                                        </FormControl>
                                        <FormMessage />
                                    </FormControlStack>
                                </FormItem>
                            );
                        }}
                    />
                ) : (
                    <FormField
                        control={form.control}
                        name="day_of_week"
                        render={({ field }) => (
                            <FormItem variant="dialog">
                                <FormLabel variant="dialog" required>
                                    <FormattedMessage {...pricingPlanMessages.day} />
                                </FormLabel>
                                <FormControlStack>
                                    <FormControl>
                                        <Select onValueChange={(val) => field.onChange([val])} value={field.value[0] ?? ''}>
                                            <SelectTrigger className="w-full">
                                                <SelectValue placeholder={`${formatMessage(pricingPlanMessages.day)}...`} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {Object.entries(DAY_OF_WEEK_TRANSLATIONS_MAP).map(([key, value]) => (
                                                    <SelectItem key={key} value={key}>
                                                        {formatMessage(value)}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormControlStack>
                            </FormItem>
                        )}
                    />
                )
            }

            <FormField
                control={form.control}
                name="rate_with_tax"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...pricingPlanMessages.rate} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder={`${formatMessage(pricingPlanMessages.rate)}...`}
                                    value={
                                        displayRate !== ''
                                            ? displayRate
                                            : field.value != null
                                              ? (field.value / 100).toFixed(2)
                                              : ''
                                    }
                                    onChange={(e) => {
                                        const inputValue = e.target.value;

                                        if (!/^\d*\.?\d{0,2}$/.test(inputValue)) return;

                                        setDisplayRate(inputValue);

                                        const numeric = parseFloat(inputValue);
                                        if (!isNaN(numeric)) {
                                            field.onChange(Math.round(numeric * 100));
                                        } else {
                                            field.onChange(undefined);
                                        }
                                    }}
                                    onBlur={() => {
                                        if (displayRate === '' || isNaN(parseFloat(displayRate))) return;
                                        const formatted = parseFloat(displayRate).toFixed(2);
                                        setDisplayRate(formatted);
                                        field.onChange(Math.round(parseFloat(formatted) * 100));
                                    }}
                                    onKeyDown={(e) => {
                                        if (['-', '+', 'e'].includes(e.key)) e.preventDefault();
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="from_time"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...pricingPlanMessages.fromHour} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input type="time" {...field} value={field.value ?? ''} />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="to_time"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...pricingPlanMessages.toHour} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input type="time" {...field} value={field.value ?? ''} />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
        </Form>
    );
}

export default TickRateForm;
